import ArticleIcon from '@mui/icons-material/Article';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LinkIcon from '@mui/icons-material/Link';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import { TagResultState } from '@ui/Utils/scan/tag/scanTypes';
import { useGetTagRedirectLink } from '@ui/Utils/scan/tag/useGetTagRedirectLink';

import { TagResultSection } from './TagResultSection';

interface AdvancedTagContentProps {
    data: TagResultState | null;
    onCopyUid: () => void;
    onCopyUrl: () => void;
    uidCopied: boolean;
    urlCopied: boolean;
}

export function AdvancedTagContent({
    data,
    onCopyUid,
    onCopyUrl,
    uidCopied,
    urlCopied,
}: Readonly<AdvancedTagContentProps>) {
    const { tagRedirectLink } = useGetTagRedirectLink(data?.serial ?? '');

    if (!data) {
        return (
            <Typography
                sx={{
                    color: 'text.secondary',
                    mt: 2,
                }}
                variant="body2"
            >
                No advanced data available.
            </Typography>
        );
    }

    return (
        <Stack spacing={2}>
            {data.type === 'nfc' && data.uid ? (
                <TagResultSection dense icon={<FingerprintIcon color="info" />} title="UID">
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} variant="body2">
                            {data.uid}
                        </Typography>
                        <Button
                            color={uidCopied ? 'success' : 'primary'}
                            onClick={onCopyUid}
                            size="small"
                            variant="outlined"
                        >
                            {uidCopied ? 'Copied' : 'Copy UID'}
                        </Button>
                    </Stack>
                </TagResultSection>
            ) : null}
            {data.type === 'nfc' && data.tech?.length ? (
                <TagResultSection dense icon={<SettingsInputAntennaIcon color="info" />} title="Technologies">
                    <Stack
                        direction="row"
                        sx={{
                            flexWrap: 'wrap',
                            gap: 1,
                            mt: 0.25,
                        }}
                    >
                        {data.tech.map((t: string) => (
                            <Chip key={t} label={t.replace('android.nfc.tech.', '')} size="small" variant="outlined" />
                        ))}
                    </Stack>
                </TagResultSection>
            ) : null}
            {data.redirectUrl ? (
                <TagResultSection dense icon={<LinkIcon color="info" />} title="Redirect URL">
                    <Chip
                        color="success"
                        label={urlCopied ? 'Copied!' : data.redirectUrl}
                        onClick={onCopyUrl}
                        size="small"
                        sx={{ cursor: 'pointer', maxWidth: '100%' }}
                        variant="outlined"
                    />
                </TagResultSection>
            ) : data.raw ? (
                <TagResultSection dense icon={<LinkIcon color="info" />} title="Redirect URL">
                    <Chip
                        color="error"
                        label={urlCopied ? 'Copied!' : data.raw}
                        onClick={onCopyUrl}
                        size="small"
                        sx={{ cursor: 'pointer', maxWidth: '100%' }}
                        variant="outlined"
                    />
                </TagResultSection>
            ) : null}
            {tagRedirectLink?.link ? (
                <TagResultSection dense icon={<LinkIcon color="info" />} title="Tag Link">
                    <Chip
                        color="success"
                        label={tagRedirectLink.link}
                        size="small"
                        sx={{ maxWidth: '100%' }}
                        variant="outlined"
                    />
                </TagResultSection>
            ) : null}
            {data.type === 'nfc' && data.texts?.length ? (
                <TagResultSection icon={<ArticleIcon color="info" />} title="Text Records">
                    <Stack spacing={1}>
                        {data.texts.map((t: { lang: string; text: string }) => (
                            <Box
                                key={t.lang + t.text.slice(0, 8)}
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
                            >
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                    variant="caption"
                                >
                                    {t.lang}
                                </Typography>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
                                    {t.text}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </TagResultSection>
            ) : null}
        </Stack>
    );
}

export default AdvancedTagContent;
